import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import userSettingsService from '../services/userSettings.service';

// ============================================================================
// TYPES
// ============================================================================

export interface ThemeColor {
  hue: number;        // 0-360
  saturation: number; // 0-100
}

// Dark-mode base palette: 'blue' = the classic blue-tinted Tailwind gray (the
// default), 'grey' = neutral near-black (Azure DevOps look). Grey is applied
// via a `theme-grey` class on <html> (see the .dark.theme-grey block in
// index.css); blue is the default and needs no class.
export type DarkPaletteVariant = 'grey' | 'blue';

// Default neutral gray (no color tint)
export const DEFAULT_THEME_COLOR: ThemeColor = { hue: 220, saturation: 0 };
export const DEFAULT_DARK_PALETTE_VARIANT: DarkPaletteVariant = 'blue';

interface ThemeContextType {
  // Theme enabled toggle
  themeEnabled: boolean;
  setThemeEnabled: (enabled: boolean) => void;

  // Light mode theme color
  lightThemeColor: ThemeColor;
  setLightThemeColor: (color: ThemeColor) => void;

  // Dark mode theme color
  darkThemeColor: ThemeColor;
  setDarkThemeColor: (color: ThemeColor) => void;

  // Dark mode base palette (grey/blue) — independent of the custom-tint toggle
  darkPaletteVariant: DarkPaletteVariant;
  setDarkPaletteVariant: (variant: DarkPaletteVariant) => void;

  // Loading state
  isLoading: boolean;
}

// Default CSS values (neutral gray - original before theme system)
const DEFAULT_CSS_LIGHT = {
  '--theme-bg-primary': '255 255 255',
  '--theme-bg-secondary': '249 250 251',
  '--theme-bg-tertiary': '243 244 246',
  '--theme-bg-elevated': '255 255 255',
  '--theme-border-primary': '229 231 235',
  '--theme-border-secondary': '209 213 219',
};

// Blue-tinted classic dark surfaces (mirrors the DEFAULT .dark ramp in
// index.css). Only the KEYS are consumed now — the "custom tints off" reset
// path removes these inline props so the stylesheet governs (blue default /
// .dark.theme-grey neutral); the values are kept as documentation.
const DEFAULT_CSS_DARK = {
  '--theme-bg-primary': '17 24 39',
  '--theme-bg-secondary': '31 41 55',
  '--theme-bg-tertiary': '55 65 81',
  '--theme-bg-elevated': '31 41 55',
  '--theme-border-primary': '75 85 99',
  '--theme-border-secondary': '107 114 128',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_PREFERENCES_KEY = 'theme_preferences';

interface CachedThemePreferences {
  themeEnabled: boolean;
  lightThemeColor: ThemeColor;
  darkThemeColor: ThemeColor;
  darkPaletteVariant: DarkPaletteVariant;
}

// Read the locally cached preferences. This is the pre-paint source of truth:
// the `dark` class is applied synchronously from localStorage (see the
// useState initializer in CollapsibleDashboard), so the palette variant has to
// resolve just as early or the first paint shows the blue ramp and then snaps
// to grey once the API round trip lands. The server value still wins once it
// arrives; this is only an optimistic head start.
export function readCachedThemePreferences(): CachedThemePreferences {
  const cached: CachedThemePreferences = {
    themeEnabled: false,
    lightThemeColor: DEFAULT_THEME_COLOR,
    darkThemeColor: DEFAULT_THEME_COLOR,
    darkPaletteVariant: DEFAULT_DARK_PALETTE_VARIANT,
  };

  try {
    const raw = localStorage.getItem(THEME_PREFERENCES_KEY);
    if (!raw) return cached;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.themeEnabled === 'boolean') cached.themeEnabled = parsed.themeEnabled;
    if (parsed?.lightThemeColor) cached.lightThemeColor = parsed.lightThemeColor;
    if (parsed?.darkThemeColor) cached.darkThemeColor = parsed.darkThemeColor;
    if (parsed?.darkPaletteVariant === 'grey' || parsed?.darkPaletteVariant === 'blue') {
      cached.darkPaletteVariant = parsed.darkPaletteVariant;
    }
  } catch {
    // Corrupt cache — fall back to defaults; the API load will repair it.
  }

  return cached;
}

// Blue is the default (no class); grey adds `theme-grey`, which only takes
// effect in dark mode (see the .dark.theme-grey block in index.css).
function applyDarkPaletteVariant(variant: DarkPaletteVariant) {
  document.documentElement.classList.toggle('theme-grey', variant === 'grey');
}

// ============================================================================
// COLOR GENERATION UTILITIES
// ============================================================================

// Convert HSL to RGB (returns "R G B" string for CSS variables)
function hslToRgb(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return `${f(0)} ${f(8)} ${f(4)}`;
}

// Generate theme CSS variables from a base color
function generateThemeVariables(color: ThemeColor | null | undefined, isDark: boolean): Record<string, string> {
  // Use safe defaults if color is missing or malformed
  const hue = color?.hue ?? DEFAULT_THEME_COLOR.hue;
  const saturation = color?.saturation ?? DEFAULT_THEME_COLOR.saturation;

  // Scale saturation - user picks 0-100, we allow up to 50% for more vivid colors
  const sat = saturation * 0.5; // Max 50% saturation for noticeable tints

  if (isDark) {
    // Dark mode: dark backgrounds with color tint
    return {
      '--theme-bg-primary': hslToRgb(hue, sat, 7),           // Very dark
      '--theme-bg-secondary': hslToRgb(hue, sat, 12),        // Dark
      '--theme-bg-tertiary': hslToRgb(hue, sat, 20),         // Medium dark
      '--theme-bg-elevated': hslToRgb(hue, sat, 12),         // Same as secondary
      '--theme-border-primary': hslToRgb(hue, sat * 0.8, 30),   // Border
      '--theme-border-secondary': hslToRgb(hue, sat * 0.8, 40), // Lighter border
    };
  } else {
    // Light mode: light backgrounds with color tint
    return {
      '--theme-bg-primary': hslToRgb(hue, sat, 99),          // Near white with tint
      '--theme-bg-secondary': hslToRgb(hue, sat * 1.2, 96),  // Noticeable tint
      '--theme-bg-tertiary': hslToRgb(hue, sat * 1.5, 92),   // More saturated tint
      '--theme-bg-elevated': hslToRgb(hue, sat * 0.5, 100),  // Slight tint
      '--theme-border-primary': hslToRgb(hue, sat * 1.2, 88),   // Light border
      '--theme-border-secondary': hslToRgb(hue, sat * 1.2, 80), // Darker border
    };
  }
}

// Apply theme variables to document root
function applyThemeVariables(lightColor: ThemeColor, darkColor: ThemeColor, enabled: boolean) {
  const root = document.documentElement;

  if (!enabled) {
    // Custom tints off: clear any inline overrides so the stylesheet governs.
    // index.css already defines these for every mode+variant (:root light,
    // .dark blue, .dark.theme-grey neutral), so removing the inline values lets
    // the grey/blue palette selection take effect for the theme-bg-* elements.
    Object.keys(DEFAULT_CSS_DARK).forEach((key) => {
      root.style.removeProperty(key);
      root.style.removeProperty(`${key}-light`);
      root.style.removeProperty(`${key}-dark`);
    });
    return;
  }

  // Generate and apply light mode variables
  const lightVars = generateThemeVariables(lightColor, false);
  Object.entries(lightVars).forEach(([key, value]) => {
    root.style.setProperty(`${key}-light`, value);
  });

  // Generate and apply dark mode variables
  const darkVars = generateThemeVariables(darkColor, true);
  Object.entries(darkVars).forEach(([key, value]) => {
    root.style.setProperty(`${key}-dark`, value);
  });

  // Apply based on current mode (light or dark class)
  const isDarkMode = root.classList.contains('dark');
  const activeVars = isDarkMode ? darkVars : lightVars;
  Object.entries(activeVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// ============================================================================
// PROVIDER
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Seed from the local cache and paint it immediately — the palette variant
  // has to be on <html> before the first commit, not after the API responds.
  const [cachedPreferences] = useState<CachedThemePreferences>(() => {
    const cached = readCachedThemePreferences();
    applyDarkPaletteVariant(cached.darkPaletteVariant);
    applyThemeVariables(cached.lightThemeColor, cached.darkThemeColor, cached.themeEnabled);
    return cached;
  });
  const [themeEnabled, setThemeEnabledState] = useState(cachedPreferences.themeEnabled);
  const [lightThemeColor, setLightThemeColorState] = useState<ThemeColor>(cachedPreferences.lightThemeColor);
  const [darkThemeColor, setDarkThemeColorState] = useState<ThemeColor>(cachedPreferences.darkThemeColor);
  const [darkPaletteVariant, setDarkPaletteVariantState] = useState<DarkPaletteVariant>(cachedPreferences.darkPaletteVariant);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved theme preferences on mount
  useEffect(() => {
    const loadThemePreferences = async () => {
      try {
        // Only try to fetch from API if user is authenticated
        if (userSettingsService.isAuthenticated()) {
          const setting = await userSettingsService.getUserSetting('theme_preferences');
          if (setting && setting.setting_value) {
            const parsed = typeof setting.setting_value === 'string'
              ? JSON.parse(setting.setting_value)
              : setting.setting_value;

            // Load themeEnabled (default false)
            if (parsed.themeEnabled !== undefined) {
              setThemeEnabledState(parsed.themeEnabled);
            }
            // Handle new format (ThemeColor objects)
            if (parsed.lightThemeColor) {
              setLightThemeColorState(parsed.lightThemeColor);
            }
            if (parsed.darkThemeColor) {
              setDarkThemeColorState(parsed.darkThemeColor);
            }
            if (parsed.darkPaletteVariant) {
              setDarkPaletteVariantState(parsed.darkPaletteVariant);
            }

            // Mirror the server value into the cache so the NEXT load paints it
            // pre-paint. Without this, a browser that only ever received the
            // palette from the API (never saved it here) flashes on every load.
            try {
              localStorage.setItem(THEME_PREFERENCES_KEY, JSON.stringify({
                themeEnabled: parsed.themeEnabled ?? false,
                lightThemeColor: parsed.lightThemeColor ?? DEFAULT_THEME_COLOR,
                darkThemeColor: parsed.darkThemeColor ?? DEFAULT_THEME_COLOR,
                darkPaletteVariant: parsed.darkPaletteVariant ?? DEFAULT_DARK_PALETTE_VARIANT,
              }));
            } catch (e) {
              console.error('Failed to cache theme preferences locally:', e);
            }
          }
        } else {
          // User not authenticated, only use localStorage
          const localSettings = localStorage.getItem(THEME_PREFERENCES_KEY);
          if (localSettings) {
            try {
              const parsed = JSON.parse(localSettings);
              if (parsed.themeEnabled !== undefined) setThemeEnabledState(parsed.themeEnabled);
              if (parsed.lightThemeColor) setLightThemeColorState(parsed.lightThemeColor);
              if (parsed.darkThemeColor) setDarkThemeColorState(parsed.darkThemeColor);
              if (parsed.darkPaletteVariant) setDarkPaletteVariantState(parsed.darkPaletteVariant);
            } catch (e) {
              console.error('Failed to parse local theme preferences:', e);
            }
          }
        }
      } catch (error: any) {
        // Only log non-404 errors (404 is expected for new users)
        if (error?.response?.status !== 404) {
          console.error('Failed to load theme preferences:', error);
        }
        // Try localStorage fallback
        const localSettings = localStorage.getItem(THEME_PREFERENCES_KEY);
        if (localSettings) {
          try {
            const parsed = JSON.parse(localSettings);
            if (parsed.themeEnabled !== undefined) setThemeEnabledState(parsed.themeEnabled);
            if (parsed.lightThemeColor) setLightThemeColorState(parsed.lightThemeColor);
            if (parsed.darkThemeColor) setDarkThemeColorState(parsed.darkThemeColor);
            if (parsed.darkPaletteVariant) setDarkPaletteVariantState(parsed.darkPaletteVariant);
          } catch (e) {
            console.error('Failed to parse local theme preferences:', e);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadThemePreferences();
  }, []);

  // Apply theme variables when colors change or themeEnabled changes. No
  // isLoading gate: the state starts at the cached values, not a placeholder,
  // so there is nothing to hold back — gating here was what deferred the
  // palette until after the round trip.
  useEffect(() => {
    applyThemeVariables(lightThemeColor, darkThemeColor, themeEnabled);
  }, [lightThemeColor, darkThemeColor, themeEnabled]);

  // Keep the `theme-grey` class in sync once the server value (or a user
  // change) supersedes the cached one applied during the first render.
  useEffect(() => {
    applyDarkPaletteVariant(darkPaletteVariant);
  }, [darkPaletteVariant]);

  // Re-apply when dark mode changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          applyThemeVariables(lightThemeColor, darkThemeColor, themeEnabled);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, [lightThemeColor, darkThemeColor, themeEnabled]);

  // Save preferences when they change
  const savePreferences = async (
    enabled: boolean,
    light: ThemeColor,
    dark: ThemeColor,
    variant: DarkPaletteVariant,
  ) => {
    const preferences = {
      themeEnabled: enabled,
      lightThemeColor: light,
      darkThemeColor: dark,
      darkPaletteVariant: variant,
    };

    // Save to localStorage immediately
    localStorage.setItem(THEME_PREFERENCES_KEY, JSON.stringify(preferences));

    // Only save to database if user is authenticated
    if (userSettingsService.isAuthenticated()) {
      try {
        await userSettingsService.saveUserSetting('theme_preferences', preferences);
      } catch (error) {
        console.error('Failed to save theme preferences to database:', error);
      }
    }
  };

  const setThemeEnabled = (enabled: boolean) => {
    setThemeEnabledState(enabled);
    savePreferences(enabled, lightThemeColor, darkThemeColor, darkPaletteVariant);
  };

  const setLightThemeColor = (color: ThemeColor) => {
    setLightThemeColorState(color);
    savePreferences(themeEnabled, color, darkThemeColor, darkPaletteVariant);
  };

  const setDarkThemeColor = (color: ThemeColor) => {
    setDarkThemeColorState(color);
    savePreferences(themeEnabled, lightThemeColor, color, darkPaletteVariant);
  };

  const setDarkPaletteVariant = (variant: DarkPaletteVariant) => {
    setDarkPaletteVariantState(variant);
    savePreferences(themeEnabled, lightThemeColor, darkThemeColor, variant);
  };

  return (
    <ThemeContext.Provider
      value={{
        themeEnabled,
        setThemeEnabled,
        lightThemeColor,
        setLightThemeColor,
        darkThemeColor,
        setDarkThemeColor,
        darkPaletteVariant,
        setDarkPaletteVariant,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

// Safe default context for when used outside provider (prevents JS errors)
const defaultThemeContext: ThemeContextType = {
  themeEnabled: false,
  setThemeEnabled: () => {},
  lightThemeColor: DEFAULT_THEME_COLOR,
  setLightThemeColor: () => {},
  darkThemeColor: DEFAULT_THEME_COLOR,
  setDarkThemeColor: () => {},
  darkPaletteVariant: DEFAULT_DARK_PALETTE_VARIANT,
  setDarkPaletteVariant: () => {},
  isLoading: false,
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  // Return safe defaults instead of throwing if used outside provider
  if (context === undefined) {
    return defaultThemeContext;
  }
  return context;
};

// Export utility for generating preview colors
export function getPreviewColor(color: ThemeColor | null | undefined, isDark: boolean): string {
  // Use safe defaults if color is missing or malformed
  const hue = color?.hue ?? DEFAULT_THEME_COLOR.hue;
  const saturation = color?.saturation ?? DEFAULT_THEME_COLOR.saturation;
  const sat = saturation * 0.5;
  const lightness = isDark ? 12 : 96;
  return `hsl(${hue}, ${sat}%, ${lightness}%)`;
}
